import { PropertyNameMapper } from '@app/shared/forms/property-mapper';
import { hasValue, isNotEmpty, memoizeWith } from '@app/util';
import { ValdrControlValidator } from '@app/valdr/models';
import { ValdrConstraints } from '@app/valdr/models/valdr-constraints';
import { updateArray, updateGroup, validate } from 'ngrx-forms';
import * as R from 'ramda';

const isPropertyNameMapper = R.is(PropertyNameMapper);

const getValidators = (
  constraints: ValdrConstraints,
  propertyNameMapper: PropertyNameMapper,
  name: string
): ValdrControlValidator[] => {
  const entityName = propertyNameMapper.getEntityName();
  const propertyConstraints = constraints[entityName];
  const propertyName = propertyNameMapper.getPropertyName(name);

  return R.values(propertyConstraints[propertyName]);
};

const getEntityValidationSchema = (validationSchema: any, mapper: PropertyNameMapper) => {
  if (mapper.isObjectEntity()) {
    return updateGroup(validationSchema);
  } else if (mapper.isArrayEntity()) {
    return updateArray(updateGroup(validationSchema));
  } else {
    throw Error('Invalid property mapper state');
  }
};

const ngrxWithValdrInternal = (constraints: ValdrConstraints, propertyNameMapper: PropertyNameMapper) => {
  if (!propertyNameMapper) {
    throw Error('Property name mapper is not defined');
  }

  const validationSchema = {};

  if (hasValue(constraints)) {
    R.forEachObjIndexed((value: string | PropertyNameMapper, name: string) => {
      if (isPropertyNameMapper(value)) {
        const objectValidationSchema = ngrxWithValdrInternal(constraints, value);
        validationSchema[name] = getEntityValidationSchema(objectValidationSchema, value);

        return validationSchema;
      } else {
        const validators = getValidators(constraints, propertyNameMapper, name);

        if (isNotEmpty(validators)) {
          validationSchema[name] = validate(validators);
        }
      }
    }, propertyNameMapper.getPropertyNameMap());
  }

  return validationSchema;
};

const cacheKeyFn = (propertyNameMapper: PropertyNameMapper) =>
  propertyNameMapper.getEntityName();

const cacheParentKeyFn = (propertyNameMapper: PropertyNameMapper) =>
  propertyNameMapper.getParentEntityName();

const updateValidationFn = (propertyNameMapper: PropertyNameMapper, constraints?: ValdrConstraints) =>
  updateGroup(ngrxWithValdrInternal(constraints, propertyNameMapper));

/**
 * Creates a memoized function that accepts property name mapper and Valdr constraints and
 * adds validators to all fields of the ngrx-forms form according to the Valdr constraints.
 * The constraints are mandatory for the first invocation of the function.
 * Any subsequent calls can be done using only the mapper.
 * Returns an update validation function that should be called with a ngrx form.
 *
 * @param {PropertyNameMapper} propertyNameMapper - property name mapper
 * @param {ValdrConstraints} constraints - valdr constraints
 * @return {any} Update validation function that should be called with a ngrx form
 */
type NgrxWithValdr = (propertyNameMapper: PropertyNameMapper, constraints?: ValdrConstraints) => Function;
export const ngrxWithValdr: NgrxWithValdr = memoizeWith(cacheKeyFn, cacheParentKeyFn, updateValidationFn);
